/**
 * Currency formatting utilities
 * Replaces hardcoded RWF references with tenant-specific currency
 */

export interface CurrencyConfig {
    code: string;
    symbol: string;
    name: string;
    locale: string;
}

// Common currencies supported by the platform
export const CURRENCIES: Record<string, CurrencyConfig> = {
    RWF: { code: 'RWF', symbol: 'FR', name: 'Rwandan Franc', locale: 'en-RW' },
    USD: { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
    EUR: { code: 'EUR', symbol: '€', name: 'Euro', locale: 'en-EU' },
    GBP: { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
    KES: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', locale: 'en-KE' },
    UGX: { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', locale: 'en-UG' },
    TZS: { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', locale: 'en-TZ' },
    NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
    GHS: { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', locale: 'en-GH' },
    ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
    INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
    BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
};

export const DEFAULT_CURRENCY = 'RWF';

/**
 * Get currency config for a code
 */
export const getCurrencyConfig = (code?: string | null): CurrencyConfig => {
    const currencyCode = code || DEFAULT_CURRENCY;
    return CURRENCIES[currencyCode] || CURRENCIES[DEFAULT_CURRENCY];
};

/**
 * Format amount with currency symbol
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'RWF', 'USD')
 * @param options - Additional formatting options
 */
export const formatCurrency = (
    amount: number | string | null | undefined,
    currencyCode?: string | null,
    options?: {
        showSymbol?: boolean;
        compact?: boolean;
        decimals?: number;
    }
): string => {
    const { showSymbol = true, compact = false, decimals } = options || {};
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
    
    if (isNaN(numAmount)) return showSymbol ? `${getCurrencyConfig(currencyCode).symbol} 0` : '0';
    
    const config = getCurrencyConfig(currencyCode);
    
    // Format the number
    let formatted: string;
    if (compact && Math.abs(numAmount) >= 1000000) {
        formatted = `${(numAmount / 1000000).toFixed(1)}M`;
    } else if (compact && Math.abs(numAmount) >= 1000) {
        formatted = `${(numAmount / 1000).toFixed(0)}K`;
    } else if (decimals !== undefined) {
        formatted = numAmount.toLocaleString(config.locale, { 
            minimumFractionDigits: decimals, 
            maximumFractionDigits: decimals 
        });
    } else {
        formatted = numAmount.toLocaleString(config.locale);
    }
    
    if (!showSymbol) return formatted;
    
    // Position symbol based on currency convention
    if (['USD', 'EUR', 'GBP'].includes(config.code)) {
        return `${config.symbol}${formatted}`;
    }
    return `${formatted} ${config.symbol}`;
};

/**
 * Format compact currency (e.g., "1.5M", "500K")
 */
export const formatCurrencyCompact = (
    amount: number | string | null | undefined,
    currencyCode?: string | null
): string => {
    return formatCurrency(amount, currencyCode, { compact: true });
};

/**
 * Get currency symbol only
 */
export const getCurrencySymbol = (currencyCode?: string | null): string => {
    return getCurrencyConfig(currencyCode).symbol;
};

/**
 * Get currency code only
 */
export const getCurrencyCode = (currencyCode?: string | null): string => {
    return getCurrencyConfig(currencyCode).code;
};
